import * as React from 'react';
import { useRef, useState } from 'react';

import EditorCodeBar from '../../../containers/Editor/EditorCodeBar';
import { addModifiers } from '../../../shared/helpers';
import { getLocalStorageItem, setLocalStorageItem } from '../../../shared/helpers/local-storage';
import EditorBottomBarLeft from '../EditorBottomBar/EditorBottomBarLeft';

import './CodeEditor.scss';
import SourceCodeEditor from './SourceCodeEditor';
import { StreakModal } from '../../../containers/Streak/StreakModal';
import { StreakProtectedModal } from '../../../containers/Streak/StreakProtectedModal';
import { StreakBar } from './StreakBar';

interface CodeEditorProps {
  onCodeChange: (newValue: any, e: any) => void;
  fullWidth: boolean;
  isSimulating: boolean;
  handleCloneAlpha: () => void;
  showPropertiesTabModal: boolean;
  settingsOptions: any;
  handleShowPropertiesTabModalState: any;
  handleSimulate: () => void;
  isMultiTab: boolean;
  handleChangeVisualization: () => void;
  visualization: boolean;
  isPythonCode: boolean;
  fetchingPost: boolean;
  fetchingPercentage: boolean;
  handleExample: () => void;
  showExample: boolean;
  isConsultant: boolean;
  isVisualization: boolean;
  emptyCode: boolean;
  currentActiveTab: number;
  path: any;
  tabs: any;
  isFetching: boolean;
  data: any;
  currentChildActiveTab: number;
  userId: string;
}

interface CodeEditorStateProps {
  activeTab: any;
  currentCode: string;
}

const CodeEditor = ({
  onCodeChange,
  fullWidth,
  isSimulating,
  handleCloneAlpha,
  showPropertiesTabModal,
  settingsOptions,
  handleShowPropertiesTabModalState,
  handleSimulate,
  isMultiTab,
  handleChangeVisualization,
  visualization,
  fetchingPercentage,
  fetchingPost,
  isPythonCode,
  handleExample,
  showExample,
  isConsultant,
  isVisualization,
  emptyCode,
  currentActiveTab,
  path,
  tabs,
  isFetching,
  data,
  currentChildActiveTab,
  userId,
}: CodeEditorProps & CodeEditorStateProps): JSX.Element => {
  const parentNode = useRef<HTMLDivElement | null>(null);
  const [themeColor, setThemeColor] = useState(getLocalStorageItem(userId, 'themeColor'));
  const handlerThemeLight = () => {
    setLocalStorageItem(userId, 'themeColor', 'light-theme');
    setThemeColor('light-theme');
  };

  const handlerThemeDark = () => {
    setLocalStorageItem(userId, 'themeColor', 'vs-dark');
    setThemeColor('vs-dark');
  };

  React.useEffect(() => {
    if (!themeColor) {
      handlerThemeDark();
    }
  }, []);

  return (
    <div className={`editor-code ${fullWidth ? 'editor-code--full-width' : ''}`} ref={parentNode}>
      <EditorCodeBar
        // @ts-expect-error withRouter(connect(...)) composition conflicts under @types/react@19:
        // connect's TOwnProps is `any`, collapsing the component's own-props to `never`.
        showPropertiesTabModal={showPropertiesTabModal}
        settingsOptions={settingsOptions}
        handleShowPropertiesTabModalState={handleShowPropertiesTabModalState}
      />
      <StreakModal parent={parentNode} />
      <StreakProtectedModal parent={parentNode} />
      <StreakBar userId={userId} />
      <div className={addModifiers('editor-code__wrapper', themeColor ? [themeColor] : ['vs-dark'])}>
        <SourceCodeEditor
          onCodeChange={onCodeChange}
          isSimulating={isSimulating}
          handleCloneAlpha={handleCloneAlpha}
          handlerThemeLight={handlerThemeLight}
          handlerThemeDark={handlerThemeDark}
          themeColor={themeColor}
        />
      </div>
      <EditorBottomBarLeft
        handleCloneAlpha={handleCloneAlpha}
        handleSimulate={handleSimulate}
        isMultiTab={isMultiTab}
        handleChangeVisualization={handleChangeVisualization}
        visualization={visualization}
        isFetching={fetchingPost || fetchingPercentage}
        isPythonCode={isPythonCode}
        handleExample={handleExample}
        showExample={showExample}
        isConsultant={isConsultant}
        isVisualization={isVisualization}
        emptyCode={emptyCode}
        currentActiveTab={currentActiveTab}
        path={path}
        data={data}
        tabs={tabs}
        isFetchingCheck={isFetching}
        currentChildActiveTab={currentChildActiveTab}
      />
    </div>
  );
};

export default CodeEditor;
